import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import type { BaseRow, RowActionButtonBaseProps } from '@openx/types/tableHelpers.ts';
import { type ComponentType, useEffect } from 'react';
import { RowActionButton } from '../parts/RowActionButton.tsx';
import { TileViewHeadLabels, type TileViewLabel } from './TileViewHeadLabels/TileViewHeadLabels.tsx';

type TableTileViewGridProps<T extends BaseRow> = {
  data: Array<T>;
  isLoading: boolean;
  TileViewComponent: ComponentType<{ item: T }>;
  TileViewLoadingComponent?: ComponentType;
  headerLabels?: Array<TileViewLabel>;
  customTileActions?: RowActionButtonBaseProps<T>[];
};

export const TableTileViewGrid = <T extends BaseRow>({
  data,
  TileViewComponent,
  TileViewLoadingComponent,
  headerLabels,
  isLoading,
  customTileActions,
}: TableTileViewGridProps<T>) => {
  const showAction = (item: T, action?: RowActionButtonBaseProps<T>) => {
    if (!action) {
      return false;
    }

    if (!action.hide) {
      return true;
    }

    if (typeof action.hide === 'boolean') {
      return !action.hide;
    } else {
      return !action.hide(item);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary dependencies are included for domain triggering logic
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [data]);

  if (isLoading && !TileViewLoadingComponent) {
    return <Loader />;
  }

  if (isLoading && TileViewLoadingComponent) {
    return <TileViewLoadingComponent />;
  }

  const displayHeadLabels = headerLabels && headerLabels.length > 0 && data.length > 0;

  return (
    <Box data-test={'tile-view-container'} sx={{ backgroundColor: 'background.grayLight' }}>
      {displayHeadLabels && <TileViewHeadLabels labelItems={headerLabels} />}

      <Box sx={{ flexGrow: 1 }}>
        <Grid
          container
          spacing={4}
          sx={{
            padding: 4,
            paddingTop: 0,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
          }}
        >
          {data.map(item => (
            <Grid
              key={item.id}
              className="tileContainer"
              sx={{
                // Ensure each card stretches to fill the row height
                display: 'flex',
                maxWidth: '100%',
                position: 'relative',
                overflow: 'hidden',

                '&:has(.tileItemContainer:hover) .tileItemContainer, &:has(.tileActions:hover) .tileItemContainer': {
                  opacity: 0.8,
                },
              }}
            >
              <Box
                className="tileItemContainer"
                sx={{
                  display: 'flex',
                  width: '100%',
                }}
              >
                <TileViewComponent item={item} key={item.id} />
              </Box>
              <Box
                className="tileActions"
                sx={{
                  position: 'absolute',
                  bottom: '0px',
                  width: '100%',
                  background: 'white',
                  paddingTop: 2,
                  paddingBottom: 2,
                  paddingRight: 2,
                  overflow: 'hidden',
                  gap: 1,
                  display: 'none',
                  justifyContent: 'flex-end',

                  '.tileContainer:has(.tileItemContainer:hover) &, &:hover': {
                    display: 'flex',
                  },
                }}
              >
                {customTileActions?.map(
                  action =>
                    showAction(item, action) && (
                      <RowActionButton
                        key={typeof action.label === 'string' ? action.label : action.label(item)}
                        {...action}
                        rowData={item}
                        color={action.color ?? 'primary'}
                      />
                    )
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};
